/**
 * Specialists Management API endpoints
 */
import { api } from './baseApi';
import type { ProviderListFilters } from '../../types/providerProfile';
import type { Specialist } from '../../types/user';

interface PaginatedResponse<T> {
  success: boolean;
  count: number;
  next: string | null;
  previous: string | null;
  total_pages: number;
  current_page: number;
  results: T[];
}

interface SpecialistResponse {
  success: boolean;
  data: Specialist;
}

export const specialistsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getSpecialists: builder.query<PaginatedResponse<Specialist>, { search?: string; page?: number; page_size?: number } & ProviderListFilters>({
      query: (params) => ({
        url: '/dashboard/specialists/',
        params,
      }),
      providesTags: ['Specialists'],
    }),
    getSpecialist: builder.query<SpecialistResponse, number>({
      query: (id) => `/dashboard/specialists/${id}/`,
      providesTags: (_result, _error, id) => [{ type: 'Specialist', id }],
    }),
    getSpecialistAvailabilitySlots: builder.query<unknown, number>({
      query: (specialistId) => `/dashboard/specialists/${specialistId}/availability-slots/`,
      providesTags: (_result, _error, id) => [{ type: 'Specialist', id }],
    }),
    createSpecialist: builder.mutation<SpecialistResponse, FormData>({
      query: (formData) => ({
        url: '/dashboard/specialists/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Specialists'],
    }),
    updateSpecialist: builder.mutation<SpecialistResponse, { id: number; data: FormData }>({
      query: ({ id, data }) => ({
        url: `/dashboard/specialists/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Specialist', id }, 'Specialists'],
    }),
    deleteSpecialist: builder.mutation<void, number>({
      query: (id) => ({
        url: `/dashboard/specialists/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Specialists'],
    }),
    activateSpecialist: builder.mutation<SpecialistResponse, number>({
      query: (id) => ({
        url: `/dashboard/specialists/${id}/activate/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Specialist', id }, 'Specialists'],
    }),
    deactivateSpecialist: builder.mutation<SpecialistResponse, number>({
      query: (id) => ({
        url: `/dashboard/specialists/${id}/deactivate/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Specialist', id }, 'Specialists'],
    }),
  }),
});

export const {
  useGetSpecialistsQuery,
  useGetSpecialistQuery,
  useGetSpecialistAvailabilitySlotsQuery,
  useCreateSpecialistMutation,
  useUpdateSpecialistMutation,
  useDeleteSpecialistMutation,
  useActivateSpecialistMutation,
  useDeactivateSpecialistMutation,
} = specialistsApi;

